import { NotifierService } from "app/services/notifierService/notifier.service";
import { Config } from "app/core/vo/config";
import { TrackingFactory } from "app/core/tracking/tracking-factory";
import { BaseTracking } from "app/core/tracking/base-tracking";
import { TrackingVO } from "app/core/tracking/trackingvo";
import { UtilityScripts } from "app/helpers/utility-scripts";
import { Disabled } from "app/core/tracking/disabled";
import { Scorm } from "app/core/tracking/scorm";
import { AICC } from "app/core/tracking/aicc";
import { Subscription ,  Observable } from "rxjs";
import { AppConstants } from "../vo/app-constants";
import { HttpService } from "app/services/http.service";
import { CourseTimerService } from "../../services/course-timer.service";
import { Scorm2004 } from "app/core/tracking/scorm2004";
import { CourseContextModelService } from "../models/course-context-model.service";
import { CMI5 } from "./cmi5";
import { AppServices } from "app/app.services";
import { XAPI } from "./xapi";
import { AdaptiveCourseModelService } from "../models/adaptive-course-model.service";
import { CourseMenuModelService } from "../models/course-menu-model.service";

export class TrackingManager extends NotifierService {
	private static _instance: TrackingManager = null;
	private static _singletonEnforcer: boolean;
	private _config: any;
	private _tracking: BaseTracking;
	private _trackingInitSubscription: Subscription;
	private _trackingVO: TrackingVO;
	private _lessonLocation: string[];
	private _trackingDisabled: boolean;
	private _structure: any;
	private _bookmark: object[];
	private _newlyCompletedScene: string;
	private _httpService: HttpService;
	private _courseTimer: CourseTimerService;
	private courseContextModel: CourseContextModelService;
	private courseMenuModel: CourseMenuModelService;
	private appService: AppServices;
	private _isPlatform: Boolean;
	private _skipPages: any[];
	private _pagesCompletedForLMSRelaunch: any[];
	/* For Adaptive courses */
	private adaptiveCourseModel: AdaptiveCourseModelService;
	private _arrAttempts: any[];
	private _failure: any[];
	private _totalScore: number;
	private _arrModuleScore: any[];
	private _arrMaxModuleScore: any[];

	constructor() {
		if (!TrackingManager._singletonEnforcer) {
			throw new Error(
				"This is a singleton class. Use TrackingManager.instance to get the instance!!"
			);
		}
		super();
	}

	public static get instance(): TrackingManager {
		if (this._instance == null) {
			this._singletonEnforcer = true;
			this._instance = new TrackingManager();
			this._singletonEnforcer = false;
			this._instance._trackingDisabled = false;
		}
		return this._instance;
	}

	public initialize(
		data,
		httpService: HttpService,
		courseTimer: CourseTimerService,
		courseContextModel: CourseContextModelService,
		appService: AppServices = null,
		adaptiveCourseModel: AdaptiveCourseModelService = null,
		courseMenuModel:CourseMenuModelService
	): void {
		this._courseTimer = courseTimer;
		this.courseContextModel = courseContextModel;
		this.adaptiveCourseModel = adaptiveCourseModel;
		this.courseMenuModel = courseMenuModel;
		this.appService = appService;
		this._skipPages = [];
		this._pagesCompletedForLMSRelaunch = [];
		this.initProperties(data, httpService);
		this.initTracking();
	}

	private initProperties(data, httpService: HttpService): void {
		this._config = data;
		this._httpService = httpService;
	}

	private initTracking(): void {
		this._tracking = TrackingFactory.instance.getTrackingObj(
			this._config,
			this._httpService,
			this.appService
		);

		this._trackingInitSubscription = this._tracking.notifier.subscribe(
			(data) => {
				console.log("Init tracking", data);
				if (data) {
					this.handleTrackingInit(data);
					this._notifier.next(data);
				}
			}
		);
		this._tracking.doInitialize();
	}

	private handleTrackingInit(data: TrackingVO): void {
		if (this._trackingInitSubscription) {
			this._trackingInitSubscription.unsubscribe();
		}
		this._trackingVO = data;
		this._trackingVO.clientId = this._config.clientID;
		if (this._tracking instanceof Disabled) {
			this._trackingDisabled = true;
		}
		this.initLessonLocation();
		/* For PT platform */
		if (TrackingManager.instance.trackingObj instanceof CMI5) {
			let CMI5obj = TrackingManager.instance.trackingObj as CMI5;
			this._isPlatform =
				CMI5obj.getLaunchURLVO().branding_profile.toLowerCase() ==
				AppConstants.PLATFORM
					? true
					: false;
		}
		/* ----------------------*/
		/* For Adaptive Scoring */
		if (!this.trackingVO.attempts) this._arrAttempts = [];
		else this._arrAttempts = this.trackingVO.attempts;

		if (!this.trackingVO.failure) this._failure = [];
		else this._failure = this._trackingVO.failure;

		if (!this.trackingVO.totalScore) this._totalScore = 0;
		else this._totalScore = this._trackingVO.totalScore;

		if (!this.trackingVO.moduleScore) this._arrModuleScore = [];
		else this._arrModuleScore = this._trackingVO.moduleScore.split(",");

		if (!this.trackingVO.maxModuleScore)
			this._arrMaxModuleScore = [];
		else this._arrMaxModuleScore = this._trackingVO.maxModuleScore.split(",");

		/* ----------------------*/
	}

	private initLessonLocation(): void {
		let lessonLocation = this._trackingVO.lessonLocation;
		console.log("lessonLocation>>>", lessonLocation);
		this._trackingVO.initialLessonLocation = lessonLocation;
		lessonLocation = (lessonLocation || "").toString().replace(/^[\s0]+$/, "");
		this._lessonLocation = [];
		if (
			lessonLocation &&
			lessonLocation !== "" &&
			lessonLocation !== "undefined"
		) {
			lessonLocation = UtilityScripts.searchReplace(lessonLocation, " ", "");
			this._lessonLocation = lessonLocation.split(AppConstants.DELIMITER1);
		}
	}

	public updateLessonLocation(type: string, value: string) {
		if (this._lessonLocation) {
			switch (type) {
				case AppConstants.LANGUAGE:
					this._lessonLocation[0] = value;
					break;
				case AppConstants.PRESENTATION_MODE:
					this._lessonLocation[1] = value;
					break;
				case AppConstants.MODULE_ID:
					this._lessonLocation[2] = value;
					break;
				case AppConstants.SCENE_ID:
					this._lessonLocation[3] = value;
					break;
				case AppConstants.PAGE_ID:
					this._lessonLocation[4] = value;
					break;
				case AppConstants.MAIN_MENU:
					this._lessonLocation = new Array(value);
					break;
			}
			this._trackingVO.lessonLocation = this._lessonLocation.join(
				AppConstants.DELIMITER1
			);
		}
	}

	public resetLessonLocation() {
		this._lessonLocation = [];
		this._trackingVO.lessonLocation = "";
	}

	public get lessonLocation() {
		return this._lessonLocation;
	}

	public initBookmarks(sessionTime: string, totalTime: string, structure: any) {
		this._structure = structure;
		if (
			this._trackingDisabled ||
			this._config.opProfile === AppConstants.DEMO_MODE
		) {
			if (this._config.opProfile === AppConstants.DEMO_MODE) {
				this._trackingVO.lessonStatus = this._config.completedVal;
			} else {
				this._trackingVO.lessonStatus = AppConstants.NOT_ATTEMTED;
			}
		}

		if (
			this._trackingVO.lessonStatus === null ||
			this._trackingVO.lessonStatus === "0" ||
			this._trackingVO.lessonStatus === " " ||
			this._trackingVO.lessonStatus === "" ||
			this._trackingVO.lessonStatus === "I" ||
			this._trackingVO.lessonStatus === undefined
		) {
			this._trackingVO.lessonStatus = AppConstants.INCOMPLETE;
		}

		this._trackingVO.lessonStatus = this._trackingVO.lessonStatus
			.substr(0, 1)
			.toLowerCase();

		console.log(
			"initBookmarks>>>",
			this._trackingVO.lessonStatus,
			this._config.completedVal
		);
		console.log(
			"initBookmarks>>>",
			this._trackingVO.bookmark,
			this._trackingDisabled,
			this._trackingVO
		);
		if (this._trackingVO.lessonStatus === this._config.completedVal) {
			this._bookmark = this.createBookmark(AppConstants.COMPLETE);
		} else if (
			this._trackingVO.bookmark === "" ||
			this._trackingVO.bookmark === "null" ||
			this._trackingVO.bookmark === undefined ||
			this._trackingVO.bookmark == null ||
			this._trackingDisabled
		) {
			this._bookmark = this.createBookmark(AppConstants.NOT_ATTEMTED);
			this._trackingVO.lessonStatus = AppConstants.INCOMPLETE;
		} else {
			this._bookmark = this.createBookmark(AppConstants.NOT_ATTEMTED);
			this.parseSavedBookmark();
		}
	}

	public saveRubricViewed(rubricViewedStr: string) {
        this._trackingVO.rubricStatus = rubricViewedStr;  
    }

    public parseRubricViewedStatus() {
        console.log('this._trackingVO.rubricViewedStatus', this._trackingVO.rubricStatus)
        return this._trackingVO.rubricStatus
    }
	
	private createBookmark(status: string): object[] {
		let modules = this._structure.moduleStructures;
		let retArray = [];
		for (let i: number = 0; i < modules.length; i++) {
			let module = modules[i];
			let scenes = module.childScenes;
			for (let j: number = 0; j < scenes.length; j++) {
				let scene = scenes[j];
				let page = scene.childPages;
				retArray.push({
					si: scene.courseScene.scene_id,
					ss: status,
					sl: status === AppConstants.COMPLETE ? page.length : 0,
				});
				if (status === AppConstants.COMPLETE) {
					for (let x = 0; x < this._structure.moduleStructures.length; x++) {
						let mod = this._structure.moduleStructures[x];
						mod.courseModule.module_status = status;
						for (let y = 0; y < mod.childScenes.length; y++) {
							let sce = mod.childScenes[y];
							sce.courseScene.status = status;
							for (let z = 0; z < sce.childPages.length; z++) {
								sce.childPages[z].status = status;
							}
						}
					}
				}
			}
		}
		return retArray;
	}

	private parseSavedBookmark(): void {
		console.log("parseSavedBookmark", this._trackingVO.bookmark);
		if (this._trackingVO.bookmark) {
			let arrBookmark = this._trackingVO.bookmark.split("|");
			if (arrBookmark[1]) {
				let arrCompltedPagesForLMS = arrBookmark[1].split(",");
				arrCompltedPagesForLMS.forEach((pageID) => {
					this.courseContextModel.markPageAsCompletedAndSkip(pageID, true);
					this.pagesCompletedForLMSRelaunch = pageID;
				});
			}
			if (arrBookmark[2]) {
				let arrSkipPages = arrBookmark[2].split(",");
				arrSkipPages.forEach((pageID) => {
					this.courseContextModel.markPageAsCompletedAndSkip(pageID);
					this.skipPages = pageID;
				});
			}
			let sceneTokens: string[] = arrBookmark[0].split(AppConstants.DELIMITER2);
			if (sceneTokens[sceneTokens.length - 1] === "") {
				sceneTokens.pop();
			}
			let counter: number = 0;
			if (!this._bookmark) {
				this._bookmark = [];
			}
			for (let tok of sceneTokens) {
				let sceneTokenElements: string[] = tok.split(AppConstants.DELIMITER1);
				this._bookmark[counter] = [];
				this._bookmark[counter]["ss"] = sceneTokenElements[0];
				this._bookmark[counter]["si"] = sceneTokenElements[2];
				this._bookmark[counter]["sl"] = parseInt(sceneTokenElements[1]);
				let mod = this._structure.moduleStructures.find(
					(mod) =>
						mod.childScenes.find(
							(scene) => scene.courseScene.scene_id === sceneTokenElements[2]
						) !== undefined
				);

				this.markSkipRubricAsCompleted(arrBookmark[2]);
				if (mod !== undefined) {
					let sce = mod.childScenes.find(
						(scene) => scene.courseScene.scene_id === sceneTokenElements[2]
					);
					if (sce !== undefined) {
						sce.courseScene.status = sceneTokenElements[0];
						for (let x = 0; x < parseInt(sceneTokenElements[1]) - 1; x++) {
							sce.childPages.filter((cPage) => {
								/*if (cPage.page_id === sce.childPages[x].page_id) {
                                    cPage.status = 'c';
                                }*/
							});
						}
						if (
							mod.childScenes.find(
								(scene) => scene.courseScene.status !== "c"
							) === undefined
						) {
							mod.courseModule.module_status = "c";
						}
					}
				}
				counter++;
			}
		}
		console.log("parseSavedBookmark this._bookmark", this._bookmark);
	}

	private async markSkipRubricAsCompleted(arrBookmark) {
		if (arrBookmark) {
			let arrPagesToBeSkipped = [];
			let arrSkipPages = arrBookmark.split(",");
			let jumpToPage = "";

			this._structure.moduleStructures.forEach((module) => {
				module.childScenes.forEach((scene) => {
					scene.childPages.forEach((page) => {
						let rubricName = UtilityScripts.getRubricName(page.page_rubric);
						if (rubricName == "testout_adaptive") {
							this._httpService
								.getXmlData(page.page_path)
								.then((pageXML: any) => {
									if (pageXML.getElementsByTagName("skipPages").length > 0) {
										for (
											let i = 0;
											i < pageXML.getElementsByTagName("skipPages").length;
											i++
										) {
											arrPagesToBeSkipped.push(
												pageXML
													.getElementsByTagName("skipPages")
													[i].textContent.split(",").length > 0
													? pageXML
															.getElementsByTagName("skipPages")
															[i].textContent.split(",")
													: pageXML.getElementsByTagName("skipPages")[i]
															.textContent
											);
										}
										let correctCount;
										for (let i = 0; i < arrPagesToBeSkipped.length; i++) {
											for (let j = 0; j < arrSkipPages.length; j++) {
												if (arrPagesToBeSkipped[i] == arrSkipPages[j]) {
													correctCount++;
													//page.status = AppConstants.COMPLETE;
												}
											}
										}
										if (correctCount >= arrPagesToBeSkipped.length) {
											jumpToPage =
												pageXML.getElementsByTagName("jumpToPageID")[0]
													.textContent;
										}
									}
								});
						}
					});
				});
			});
			if (jumpToPage != "") {
				this._structure.moduleStructures.forEach((module) => {
					module.childScenes.forEach((scene) => {
						scene.childPages.forEach((page) => {
							if (page.page_id != jumpToPage) {
								page.status = AppConstants.COMPLETE;
							} else if (page.page_id == jumpToPage) {
								page.status = AppConstants.COMPLETE;
								this.courseContextModel.updateCompletedPagesInCourse();
								return;
							}
						});
					});
				});
			}
		}
	}

	private updateBookmarkArray(si: string, sl: number, ss: string): void {
		console.log("this._bookmark", si, sl, ss, this._bookmark);
		for (let item of this._bookmark) {
			//let item = this._bookmark[props];
			if (item["si"] === si) {
				if (sl > item["sl"]) {
					item["sl"] = sl;
				}
				if (item["ss"] !== "c") {
					item["ss"] = ss;
					/*if (ss === 'c') { // it is used inside digital libaray, not in use
                        this._newlyCompletedScene = si;
                    }*/
				}
				break;
			} else {
				let itemMod = this._structure.moduleStructures.find((mod) =>
					mod.childScenes.find((sce) => sce.courseScene.scene_id === item["si"])
				);
				let itemSce = itemMod.childScenes.find(
					(sce) => sce.courseScene.scene_id === item["si"]
				);
				if (itemSce && itemSce.courseScene.status === "c") {
					item["ss"] = "c";
					item["sl"] = itemSce.childPages.filter(
						(childPage) =>
							childPage.page_language ===
							this._trackingVO.lessonLocation.split(",")[0]
					).length;
				}
				if (
					itemMod &&
					itemMod.childScenes.find(
						(scene) => scene.courseScene.status !== "c"
					) === undefined
				) {
					itemMod.courseModule.module_status = "c";
				}
			}
		}
		console.log("this._bookmark fter", si, sl, ss, this._bookmark);
	}

	public updateBookmark(si: string, sl: number, ss: string) {
		// si = scene id, sl= scene location, ss = scene status
		//if (!this._trackingDisabled) {
		this.updateBookmarkArray(si, sl, ss);
		this.saveBookmark();
		//}
	}

	/**
	 * Updates the current session time and total time in the course.
	 */
	public updateBookmarkedTime() {
		let sessionTime = this._courseTimer.sessionTimeString;
		let totalTime = this._courseTimer.totalTimeString;
		// In case the Tracking VO isn't yet set up (since this is called in statements.ts), don't try to set any times just yet.
		if (typeof this._trackingVO !== "undefined") {
			this.setBookmarkedTime(sessionTime, totalTime);
		}
	}

	public setBookmarkedTime(sessionTime: string, totalTime: string) {
		this._trackingVO.sessionTime = sessionTime;
		this._trackingVO.totalTime = totalTime;
		this._trackingVO.elt_total_time = totalTime;
	}

	public saveBookmark(): void {
		let strBookmark: string = "";
		let arrFinalSkipPages = [];
		let arrFinalCompletedPagesForLMSRelaunch = [];
		if (this._bookmark) {
			for (const item of this._bookmark) {
				if (!isNaN(item["sl"])) {
					strBookmark += item["ss"] + "," + item["sl"] + "," + item["si"] + ";";
				}
			}
			this.courseContextModel.courseStructure.moduleStructures.forEach(
				(module) => {
					module.childScenes.forEach((scene) => {
						scene.childPages.forEach((page) => {
							this._skipPages.forEach((skipPageId) => {
								if (skipPageId == page.page_id) {
									if (scene.courseScene.status != AppConstants.COMPLETE) {
										arrFinalSkipPages.push(skipPageId);
									}
								}
							});
							this._pagesCompletedForLMSRelaunch.forEach(
								(pageIDForCompletedPagesForLMSRelaunch) => {
									if (pageIDForCompletedPagesForLMSRelaunch == page.page_id) {
										arrFinalCompletedPagesForLMSRelaunch.push(
											pageIDForCompletedPagesForLMSRelaunch
										);
									}
								}
							);
						});
					});
				}
			);
			if (arrFinalCompletedPagesForLMSRelaunch.length > 0) {
				this._pagesCompletedForLMSRelaunch = [];
				strBookmark += "|";

				arrFinalCompletedPagesForLMSRelaunch.forEach((pageID, index) => {
					this._pagesCompletedForLMSRelaunch.push(pageID);
					if (index < arrFinalCompletedPagesForLMSRelaunch.length - 1)
						strBookmark += pageID + ",";
					else strBookmark += pageID;
				});
			}
			if (arrFinalSkipPages.length > 0) {
				this._skipPages = [];
				strBookmark += "|";

				arrFinalSkipPages.forEach((pageID, index) => {
					this._skipPages.push(pageID);
					if (index < arrFinalSkipPages.length - 1) strBookmark += pageID + ",";
					else strBookmark += pageID;
				});
			}
			this._trackingVO.bookmark = strBookmark;
			console.log("save bookmark", this._trackingVO.bookmark);
		}
	}
	public saveData(isTimeChanged: boolean = true) {
		console.log("save data called", isTimeChanged);
		//console.log("Status ",this.courseContextModel.currentPageInfo.actual_status);
		if (isTimeChanged) {
			// ---------- For Adaptive ------------------
			this.trackingVO.attempts = this._arrAttempts;
			this.trackingVO.failure = this._failure;
			this.trackingVO.penmanName = this.adaptiveCourseModel.strPenmanName;
			this.trackingVO.courseTopic = this.adaptiveCourseModel.strCourseTopic;
			this.trackingVO.learningType = this.adaptiveCourseModel.strLearningType;
			this.trackingVO.moduleVersion = this.adaptiveCourseModel.strModuleVersion;
			this.trackingVO.accessabilityOption =
				this.adaptiveCourseModel.strAccessibilityOption;
			if(this.adaptiveCourseModel.hasAdaptiveXML){
				let currentModuleId = this.courseContextModel.getPageInfoById(
					this.courseContextModel.currentPageInfo.page_id
				).module_id;
				let moduleFound = false;
				let hasModuleLevelComplianceType = false;
				this.adaptiveCourseModel.arrModuleShortname.forEach((modName) => {
					if(modName.complianceTypeID || modName.complianceTypeName){
						hasModuleLevelComplianceType = true;
					}
					if (currentModuleId === modName.module_id){
						moduleFound = true;
						this.trackingVO.moduleShortname = modName.module_short_name;
						this.trackingVO.complianceTypeID = modName.complianceTypeID;
						this.trackingVO.complianceTypeName = modName.complianceTypeName;
					} else if(!moduleFound){
						this.trackingVO.moduleShortname = "";
						this.trackingVO.complianceTypeID = "";
						this.trackingVO.complianceTypeName = "";
					}
				});
				if(!hasModuleLevelComplianceType){
					this.trackingVO.complianceTypeID = this.adaptiveCourseModel.strComplianceTypeID;
					this.trackingVO.complianceTypeName = this.adaptiveCourseModel.strComplianceTypeName;
				}
			} else {
				this.trackingVO.complianceTypeID = this.courseMenuModel.strComplianceTypeID;//this.courseMenuModel.tileImages.filter((tile) => tile.id == this.courseContextModel.currentModuleID())[0].complianceTypeID
				this.trackingVO.complianceTypeName = this.courseMenuModel.strComplianceTypeName;//this.courseMenuModel.tileImages.filter((tile) => tile.id == this.courseContextModel.currentModuleID())[0].complianceTypeName
			}
			this.trackingVO.moduleScore = this._arrModuleScore.join(",");
			this.trackingVO.maxModuleScore = this._arrMaxModuleScore.join(",");
			// --------------------------------------
			this.saveBookmark();
			this.updateBookmarkedTime();
			if (this._tracking) {
				if (
					this._tracking instanceof Scorm ||
					this._tracking instanceof Scorm2004
				) {
					let success: boolean = this._tracking.doCommit();
					if (!success) {
						alert("Error: Bookmark not saved");
					}
				} else if (this._tracking instanceof AICC) {
					this._tracking.doCommit();
				} else if (this._tracking instanceof XAPI) {
					this._tracking.doCommit();
				} else if (this._tracking instanceof CMI5) {
					this._tracking.doCommit();
				}
				if (this._tracking instanceof Scorm2004) {
					if (this.courseContextModel) {
						if (
							this.courseContextModel.currentPageInfo.status ==
							AppConstants.NOT_ATTEMTED
						)
							this._tracking.updateScreenInteraction();
					}
				}
			}
		}
	}

	public doFinish() {
		this._tracking.doFinish();
	}

	public get trackingObj(): BaseTracking {
		return this._tracking;
	}

	public get trackingVO(): TrackingVO {
		return this._trackingVO;
	}

	public get config(): any {
		return this._config;
	}
	/* This method returns the status of page before sending Interactions*/
	public checkPageStatus() {
		//console.log("checkPageStatus ",this.courseContextModel.currentPageInfo.actual_status);
		return this.courseContextModel.currentPageInfo.actual_status ==
			AppConstants.NOT_ATTEMTED
			? true
			: false;
	}

	public courseComplete() {
		this._trackingVO.completionDate = new Date().getTime();
		this._trackingVO.lessonStatus = this._config.completedVal;
		this._trackingVO.score = 100;
	}

	public completionDate(): string {
		return UtilityScripts.dateToYMD(
			new Date(this._trackingVO.completionDate),
			this._trackingVO.lessonLocation.split(",")[0]
		);
	}

	public userName(): string {
		if (this._trackingDisabled) {
			return "Demo Course - Not Valid Certificate";
		}
		const student = this._trackingVO.studentName.split(",");
		if (student[1]) return student[1] + " " + student[0];
		else return student[0];
	}
	public get opProfile() {
		return this._config.opProfile;
	}
	public set opProfile(val) {
		this._config.opProfile = val;
	}
	public get courseStatus() {
		return this._trackingVO.lessonStatus;
	}
	public get isPlatform() {
		return this._isPlatform;
	}
	public set skipPages(val) {
		this._skipPages.push(val);
	}
	public set pagesCompletedForLMSRelaunch(val) {
		this._pagesCompletedForLMSRelaunch.push(val);
	}
	public set arrAttempts(obj) {
		if (!this.avoidDuplicateEntries(obj.val)) {
			if (obj.ind < this._arrAttempts.length) {
				this._arrAttempts[obj.ind].push(obj.val);
			} else {
				while (this._arrAttempts.length < obj.ind) {
					this._arrAttempts.push([]);
				}
				this._arrAttempts.push([obj.val]);
			}
		}
	}
	public set failure(val) {
		if (!this.avoidDuplicateEntries(val)) this._failure.push(val);
	}
	public set totalScore(val) {
		this._totalScore += val;
	}
	public setCurrentModuleScore(val) {
		this.courseContextModel.courseStructure.moduleStructures.forEach(
			(module, index) => {
				if (
					module.courseModule.module_id ==
					this.courseContextModel.getPageInfoById(
						this.courseContextModel.currentPageInfo.page_id
					).module_id
				) {
					if (
						this._arrModuleScore[index] == "" ||
						this._arrModuleScore[index] == null
					) {
						this._arrModuleScore[index] = val;
					} else {
						this._arrModuleScore[index] =
							parseInt(this._arrModuleScore[index]) + val;
					}
					this.trackingVO.currentModuleScore = this._arrModuleScore[index];
				}
			}
		);
	}
	public setMaxScoreInModuleAndCourse(val) {
		this.trackingVO.maxTotalScore += val;
		this.courseContextModel.courseStructure.moduleStructures.forEach(
			(module, index) => {
				if (
					module.courseModule.module_id ==
					this.courseContextModel.getPageInfoById(
						this.courseContextModel.currentPageInfo.page_id
					).module_id
				) {
					if (
						this._arrMaxModuleScore[index] == "" ||
						this._arrMaxModuleScore[index] == null
					) {
						this._arrMaxModuleScore[index] = val;
					} else {
						this._arrMaxModuleScore[index] =
							parseInt(this._arrMaxModuleScore[index]) + val;
					}
					this.trackingVO.currentModuleMaxScore =
						this._arrMaxModuleScore[index];
				}
			}
		);
	}
	public setTotalScoreAndMaxTotalScore() {
		this.trackingVO.maxTotalScore = 0;
		this._arrMaxModuleScore.forEach((val) => {
			if (val) this.trackingVO.maxTotalScore += parseInt(val);
		});
		this.trackingVO.totalScore = 0;
		this._arrModuleScore.forEach((val) => {
			if (val) this.trackingVO.totalScore += parseInt(val);
		});
	}
	public setCurrentModuleScoreAndMaxModuleScore(module_id) {
		this.courseContextModel.courseStructure.moduleStructures.forEach(
			(module, index) => {
				if (module.courseModule.module_id == module_id) {
					this.trackingVO.currentModuleMaxScore =
						this._arrMaxModuleScore[index];
					this.trackingVO.currentModuleScore = this._arrModuleScore[index];
				}
			}
		);
	}
	private avoidDuplicateEntries(val) {
		let duplicate = false;
		this._arrAttempts.forEach((attempt) => {
			if (attempt.val == val) return true;
		});
		this._failure.forEach((lpName) => {
			if (lpName == val) return true;
		});
		return false;
	}
}